import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Settings, Calendar, Save, RefreshCw, CheckCircle2, Clock, DollarSign, CalendarDays, AlertCircle, FileText, Plus } from 'lucide-react';
import ProduccionLayout from '@/components/ProduccionLayout';
import { formatearFecha } from '@/utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Planilla({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [calendarioPlanillas, setCalendarioPlanillas] = useState([]);
  const [puedeCrearProximoAnio, setPuedeCrearProximoAnio] = useState(false);
  const [creandoProximoAnio, setCreandoProximoAnio] = useState(false);
  
  // Tab activa: 'planillas' o 'calendario'
  const [activeTab, setActiveTab] = useState('planillas');
  
  // Días de la semana
  const DIAS_SEMANA = [
    { value: 0, label: 'Lunes' },
    { value: 1, label: 'Martes' },
    { value: 2, label: 'Miércoles' },
    { value: 3, label: 'Jueves' },
    { value: 4, label: 'Viernes' },
    { value: 5, label: 'Sábado' },
    { value: 6, label: 'Domingo' },
  ];
  
  // Form state para configuración
  const [formConfig, setFormConfig] = useState({
    fecha_inicio: '',
    periodicidad: 'quincenal',
    dias_libres: [], // Vacío por defecto - el usuario selecciona sus días libres
    dia_pago_semanal: 5, // Sábado por defecto
    dias_cierre_antes_pago: 2 // 2 días antes del pago termina el período
  });

  // Cargar configuración existente
  useEffect(() => {
    fetchConfig();
    verificarPuedeCrearProximoAnio();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/planilla/config`);
      if (response.data) {
        setConfig(response.data);
        setFormConfig({
          fecha_inicio: response.data.fecha_inicio || '',
          periodicidad: response.data.periodicidad || 'quincenal',
          dias_libres: response.data.dias_libres || [],
          dia_pago_semanal: response.data.dia_pago_semanal ?? 5,
          dias_cierre_antes_pago: response.data.dias_cierre_antes_pago ?? 2
        });
        // Cargar calendario si hay configuración
        if (response.data.fecha_inicio) {
          fetchCalendario();
        }
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      // Si no existe configuración, mostrar modal
      if (error.response?.status === 404) {
        setShowConfigModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendario = async () => {
    try {
      const response = await axios.get(`${API}/planilla/calendario`);
      // Filtrar solo planillas del año actual y anteriores
      const anioActual = new Date().getFullYear();
      const planillasFiltradas = (response.data || []).filter(p => {
        const anioPlanilla = parseInt(p.fecha_pago.split('-')[0]);
        return anioPlanilla <= anioActual;
      });
      setCalendarioPlanillas(planillasFiltradas);
    } catch (error) {
      console.error('Error al cargar calendario:', error);
    }
  };

  const verificarPuedeCrearProximoAnio = async () => {
    try {
      const response = await axios.get(`${API}/planilla/puede-crear-proximo-anio`);
      setPuedeCrearProximoAnio(response.data.puede_crear);
    } catch (error) {
      console.error('Error al verificar próximo año:', error);
    }
  };

  const crearCalendarioProximoAnio = async () => {
    setCreandoProximoAnio(true);
    try {
      const response = await axios.post(`${API}/planilla/crear-calendario-proximo-anio`);
      toast.success(response.data.mensaje);
      fetchCalendario();
      verificarPuedeCrearProximoAnio();
    } catch (error) {
      console.error('Error al crear calendario:', error);
      toast.error(error.response?.data?.detail || 'Error al crear calendario del próximo año');
    } finally {
      setCreandoProximoAnio(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!formConfig.fecha_inicio) {
      toast.error('Ingrese la fecha de inicio de la primera planilla');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/planilla/config`, {
        ...formConfig,
        user_id: user?.id
      });
      
      setConfig(response.data.config);
      setCalendarioPlanillas(response.data.calendario || []);
      setShowConfigModal(false);
      toast.success('Configuración guardada y calendario generado');
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  // Toggle día libre
  const toggleDiaLibre = (dia) => {
    setFormConfig(prev => {
      const diasActuales = prev.dias_libres || [];
      if (diasActuales.includes(dia)) {
        return { ...prev, dias_libres: diasActuales.filter(d => d !== dia) };
      } else {
        return { ...prev, dias_libres: [...diasActuales, dia] };
      }
    });
  };

  // Obtener nombre de días libres
  const getDiasLibresNombres = () => {
    if (!config?.dias_libres || config.dias_libres.length === 0) return 'Ninguno';
    return config.dias_libres
      .sort((a, b) => a - b)
      .map(d => DIAS_SEMANA.find(ds => ds.value === d)?.label)
      .filter(Boolean)
      .join(', ');
  };

  // Obtener nombre del día de pago
  const getDiaPagoNombre = () => {
    const dia = config?.dia_pago_semanal ?? 5;
    return DIAS_SEMANA.find(d => d.value === dia)?.label || 'Sábado';
  };

  const regenerarCalendario = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/planilla/regenerar-calendario`, {
        user_id: user?.id
      });
      setCalendarioPlanillas(response.data || []);
      toast.success('Calendario regenerado exitosamente');
    } catch (error) {
      console.error('Error al regenerar calendario:', error);
      toast.error('Error al regenerar calendario');
    } finally {
      setSaving(false);
    }
  };

  // Obtener la próxima fecha de pago
  const proximaFechaPago = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const proxima = calendarioPlanillas.find(p => {
      const fechaPago = new Date(p.fecha_pago + 'T00:00:00');
      return fechaPago >= hoy;
    });
    
    return proxima;
  }, [calendarioPlanillas]);

  // Formatear fecha para mostrar
  const formatFechaPago = (fechaStr) => {
    if (!fechaStr) return '-';
    const fecha = new Date(fechaStr + 'T00:00:00');
    return fecha.toLocaleDateString('es-CR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Determinar estado de una planilla
  const getEstadoPlanilla = (planilla) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaPago = new Date(planilla.fecha_pago + 'T00:00:00');
    const fechaInicio = new Date(planilla.periodo_inicio + 'T00:00:00');
    const fechaFin = new Date(planilla.periodo_fin + 'T00:00:00');
    
    if (fechaPago < hoy) {
      return { label: 'Cerrada', color: 'bg-gray-100 text-gray-700' };
    } else if (hoy >= fechaInicio && hoy <= fechaFin) {
      return { label: 'En curso', color: 'bg-blue-100 text-blue-700' };
    } else if (fechaPago.getTime() === proximaFechaPago?.fecha_pago && new Date(proximaFechaPago?.fecha_pago + 'T00:00:00').getTime() === fechaPago.getTime()) {
      return { label: 'Próxima', color: 'bg-emerald-100 text-emerald-700' };
    } else {
      return { label: 'Pendiente', color: 'bg-amber-100 text-amber-700' };
    }
  };

  if (loading) {
    return (
      <ProduccionLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        </div>
      </ProduccionLayout>
    );
  }

  return (
    <ProduccionLayout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Planilla</h1>
              <p className="text-sm text-gray-500">Gestión de períodos de pago</p>
            </div>
          </div>
          <Button
            onClick={() => setShowConfigModal(true)}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="config-planilla-btn"
          >
            <Settings className="w-4 h-4" />
            Configuración
          </Button>
        </div>

        {/* Tabs de navegación */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-4">
            <button
              onClick={() => setActiveTab('planillas')}
              className={`flex items-center px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'planillas'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-4 h-4 mr-2" />
              Planillas
            </button>
            <button
              onClick={() => setActiveTab('calendario')}
              className={`flex items-center px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'calendario'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Calendario
            </button>
          </nav>
        </div>

        {/* Contenido según tab activa */}
        {activeTab === 'planillas' && (
          <div>
            {/* Vista principal: Crear Planilla */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Crear Planilla
                </CardTitle>
                <CardDescription>
                  Seleccione un período de pago para generar la planilla con los días registrados
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!config ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Primero debe configurar el calendario de planillas</p>
                    <Button onClick={() => setShowConfigModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                      <Settings className="w-4 h-4 mr-2" />
                      Configurar Planilla
                    </Button>
                  </div>
                ) : calendarioPlanillas.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No hay períodos de pago generados</p>
                    <Button onClick={() => setActiveTab('calendario')} variant="outline">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Ver Calendario
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Próxima planilla a crear */}
                    {proximaFechaPago && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-emerald-700">Próxima planilla a crear</p>
                            <p className="text-lg font-bold text-emerald-900">
                              Planilla #{proximaFechaPago.numero}
                            </p>
                            <p className="text-sm text-emerald-600">
                              Período: {formatearFecha(proximaFechaPago.periodo_inicio)} - {formatearFecha(proximaFechaPago.periodo_fin)}
                            </p>
                            <p className="text-sm text-emerald-600">
                              Fecha de pago: {formatearFecha(proximaFechaPago.fecha_pago)}
                            </p>
                          </div>
                          <Button 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            disabled
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Crear Planilla
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-3 italic">
                          * Esta funcionalidad se conectará con el módulo "Registro de Labores" para calcular los pagos
                        </p>
                      </div>
                    )}

                    {/* Lista de planillas pendientes */}
                    <div className="mt-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Planillas pendientes de crear</h3>
                      <div className="space-y-2">
                        {calendarioPlanillas
                          .filter(p => {
                            const hoy = new Date();
                            hoy.setHours(0, 0, 0, 0);
                            const fechaPago = new Date(p.fecha_pago + 'T00:00:00');
                            return fechaPago >= hoy;
                          })
                          .slice(0, 5)
                          .map((planilla) => (
                            <div 
                              key={planilla.numero}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className="bg-white">
                                  #{planilla.numero}
                                </Badge>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {formatearFecha(planilla.periodo_inicio)} - {formatearFecha(planilla.periodo_fin)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Pago: {formatearFecha(planilla.fecha_pago)}
                                  </p>
                                </div>
                              </div>
                              <Badge className={getEstadoPlanilla(planilla).color}>
                                {getEstadoPlanilla(planilla).label}
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'calendario' && (
          <div>
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Próxima Fecha de Pago */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-700">Próximo Pago</p>
                    <p className="text-lg font-bold text-emerald-900">
                      {proximaFechaPago ? formatearFecha(proximaFechaPago.fecha_pago) : 'No configurado'}
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">
                      {proximaFechaPago ? `Planilla #${proximaFechaPago.numero}` : 'Configure el calendario'}
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-200 rounded-full">
                    <Calendar className="w-6 h-6 text-emerald-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Periodicidad */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-700">Periodicidad</p>
                    <p className="text-lg font-bold text-blue-900 capitalize">
                      {config?.periodicidad || 'No configurado'}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {config?.periodicidad === 'quincenal' ? 'Cada 15 días' : 
                       config?.periodicidad === 'semanal' ? 'Cada 7 días' : 
                       config?.periodicidad === 'bisemanal' ? 'Cada 12 días laborables' :
                       config?.periodicidad === 'mensual' ? 'Cada mes' : 'Configure'}
                    </p>
                    {config?.dias_libres?.length > 0 && (
                      <p className="text-xs text-blue-500 mt-0.5">
                        Días libres: {getDiasLibresNombres()}
                      </p>
                    )}
                    {config && (
                      <p className="text-xs text-blue-500 mt-0.5">
                        Pago: {getDiaPagoNombre()} • Cierre: {config.dias_cierre_antes_pago || 0} días antes
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-blue-200 rounded-full">
                    <Clock className="w-6 h-6 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Total Planillas Generadas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-700">Planillas Generadas</p>
                    <p className="text-2xl font-bold text-purple-900">{calendarioPlanillas.length}</p>
                    <p className="text-xs text-purple-600 mt-1">Para el año en curso</p>
                  </div>
                  <div className="p-3 bg-purple-200 rounded-full">
                    <CalendarDays className="w-6 h-6 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Calendario de Planillas */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-emerald-600" />
                Calendario de Planillas
              </CardTitle>
              <CardDescription>
                Fechas de pago generadas automáticamente según la configuración
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {puedeCrearProximoAnio && (
                <Button
                  onClick={crearCalendarioProximoAnio}
                  variant="outline"
                  size="sm"
                  disabled={creandoProximoAnio}
                  className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Calendar className={`w-4 h-4 ${creandoProximoAnio ? 'animate-spin' : ''}`} />
                  Crear {new Date().getFullYear() + 1}
                </Button>
              )}
              {config && (
                <Button
                  onClick={regenerarCalendario}
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                  Regenerar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Nota sobre creación de calendario del próximo año */}
            {puedeCrearProximoAnio && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Diciembre:</strong> Ya puede crear el calendario de planillas del año {new Date().getFullYear() + 1}. 
                  Use el botón "Crear {new Date().getFullYear() + 1}" arriba.
                </p>
              </div>
            )}
            {calendarioPlanillas.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No hay calendario de planillas configurado</p>
                <Button onClick={() => setShowConfigModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Settings className="w-4 h-4 mr-2" />
                  Configurar Planilla
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-emerald-50">
                      <TableHead className="font-semibold">#</TableHead>
                      <TableHead className="font-semibold">Período</TableHead>
                      <TableHead className="font-semibold">Fecha de Pago</TableHead>
                      <TableHead className="font-semibold">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calendarioPlanillas.map((planilla) => {
                      const estado = getEstadoPlanilla(planilla);
                      return (
                        <TableRow 
                          key={planilla.numero}
                          className={proximaFechaPago?.numero === planilla.numero ? 'bg-emerald-50' : ''}
                        >
                          <TableCell className="font-medium">
                            Planilla #{planilla.numero}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {formatearFecha(planilla.periodo_inicio)} - {formatearFecha(planilla.periodo_fin)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {proximaFechaPago?.numero === planilla.numero && (
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              )}
                              <span className={proximaFechaPago?.numero === planilla.numero ? 'font-semibold text-emerald-700' : ''}>
                                {formatFechaPago(planilla.fecha_pago)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={estado.color}>
                              {estado.label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {/* Modal de Configuración */}
        <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-emerald-600" />
                Configuración de Planilla
              </DialogTitle>
              <DialogDescription>
                Configure la fecha de inicio y periodicidad para generar el calendario de pagos
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Fecha de Inicio */}
              <div className="space-y-2">
                <Label htmlFor="fecha_inicio">
                  Fecha de Inicio de Primera Planilla *
                </Label>
                <Input
                  id="fecha_inicio"
                  type="date"
                  value={formConfig.fecha_inicio}
                  onChange={(e) => setFormConfig({ ...formConfig, fecha_inicio: e.target.value })}
                  className="w-full"
                  data-testid="fecha-inicio-input"
                />
                <p className="text-xs text-gray-500">
                  Esta será la fecha de pago de la primera planilla
                </p>
              </div>

              {/* Periodicidad */}
              <div className="space-y-2">
                <Label htmlFor="periodicidad">Periodicidad *</Label>
                <Select
                  value={formConfig.periodicidad}
                  onValueChange={(value) => setFormConfig({ ...formConfig, periodicidad: value })}
                >
                  <SelectTrigger data-testid="periodicidad-select">
                    <SelectValue placeholder="Seleccione periodicidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semanal">Semanal (cada 7 días)</SelectItem>
                    <SelectItem value="bisemanal">Bisemanal (cada 12 días laborables)</SelectItem>
                    <SelectItem value="quincenal">Quincenal (cada 15 días)</SelectItem>
                    <SelectItem value="mensual">Mensual (cada mes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Días Libres - Solo visible si es bisemanal */}
              {formConfig.periodicidad === 'bisemanal' && (
                <div className="space-y-2">
                  <Label>Días Libres (no se cuentan en los 12 días)</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Seleccione los días que NO se trabajan para excluirlos del cálculo
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DIAS_SEMANA.map((dia) => (
                      <button
                        key={dia.value}
                        type="button"
                        onClick={() => toggleDiaLibre(dia.value)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          formConfig.dias_libres?.includes(dia.value)
                            ? 'bg-red-100 border-red-300 text-red-700'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {dia.label}
                        {formConfig.dias_libres?.includes(dia.value) && ' ✓'}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Días libres seleccionados: {formConfig.dias_libres?.length || 0}
                  </p>
                </div>
              )}

              {/* Día de Pago Semanal */}
              <div className="space-y-2">
                <Label>¿Qué día de la semana se paga?</Label>
                <Select
                  value={formConfig.dia_pago_semanal?.toString()}
                  onValueChange={(value) => setFormConfig({ ...formConfig, dia_pago_semanal: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione día de pago" />
                  </SelectTrigger>
                  <SelectContent>
                    {DIAS_SEMANA.map((dia) => (
                      <SelectItem key={dia.value} value={dia.value.toString()}>
                        {dia.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Días de Cierre antes del Pago */}
              <div className="space-y-2">
                <Label>¿Cuántos días antes del pago termina el período de trabajo?</Label>
                <Select
                  value={formConfig.dias_cierre_antes_pago?.toString()}
                  onValueChange={(value) => setFormConfig({ ...formConfig, dias_cierre_antes_pago: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione días" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">El mismo día del pago</SelectItem>
                    <SelectItem value="1">1 día antes</SelectItem>
                    <SelectItem value="2">2 días antes</SelectItem>
                    <SelectItem value="3">3 días antes</SelectItem>
                    <SelectItem value="4">4 días antes</SelectItem>
                    <SelectItem value="5">5 días antes</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Ejemplo: Si paga los sábados y el período termina 2 días antes, el período cierra los jueves.
                </p>
              </div>

              {/* Info adicional */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Nota:</strong> El calendario se generará automáticamente para todo el año en curso. 
                  {formConfig.periodicidad === 'bisemanal' && (
                    <span> En modo bisemanal, se contarán solo los días laborables (excluyendo días libres) para calcular cada período de 12 días.</span>
                  )}
                  {' '}El módulo "Registro de Labores" usará estas fechas para asignar el período de planilla correspondiente.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowConfigModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveConfig}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-config-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar y Generar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ProduccionLayout>
  );
}
